import { createContext, useContext, useState } from 'react';

import {
  TFilterDefaultsContext,
  TFilterDefaultsProviderProps,
} from '@/shared/components/ui/data-table/hooks/use-filter-params/filter-defaults-context.types';
import { IGNORE_AS_FILTERS } from '@/shared/components/ui/data-table/hooks/use-filter-params/use-filter-params.constants';
import { normalizeSortedParam } from '@/shared/components/ui/data-table/hooks/use-filter-params/use-filter-params.utils';

const FilterDefaultsContext = createContext<TFilterDefaultsContext | undefined>(
  undefined,
);

export const useFilterDefaults = () => useContext(FilterDefaultsContext);

export const FilterDefaultsProvider = ({
  children,
}: TFilterDefaultsProviderProps) => {
  // State, not a ref: registering or clearing a resolved default must re-render
  // consumers so the "Reset filters" button re-evaluates isAtDefaults — e.g.
  // right after a default is saved via the gear while the URL already matches.
  const [defaults, setDefaults] = useState<Record<string, string>>({});

  const registerDefault = (key: string, value: string) => {
    setDefaults((prev) =>
      prev[key] === value ? prev : { ...prev, [key]: value },
    );
  };

  const unregisterDefault = (key: string) => {
    setDefaults((prev) => {
      if (!(key in prev)) return prev;
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });
  };

  const getDefaults = () => defaults;

  const isAtDefaults = (searchParams: URLSearchParams): boolean => {
    const activeParams = [...searchParams.entries()].filter(
      ([key]) => !IGNORE_AS_FILTERS.includes(key),
    );

    if (activeParams.length !== Object.keys(defaults).length) return false;

    return activeParams.every(
      ([key, value]) =>
        normalizeSortedParam(defaults[key] ?? '') ===
        normalizeSortedParam(value),
    );
  };

  return (
    <FilterDefaultsContext
      value={{ registerDefault, unregisterDefault, getDefaults, isAtDefaults }}
    >
      {children}
    </FilterDefaultsContext>
  );
};
